import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION } from '../config/bookmakers'
import { ALL_SPORTS, SPORT_DISPLAY_NAMES } from '../config/sports'
import { MARKET_TYPE_MAPPING } from '../config/markets'
import SubjectImage from '../components/SubjectImage'
import { convertOdds } from '../utils/oddsConverter'
import {
  saveUserFilter, getUserFilters, deleteUserFilter,
  setDefaultFilter, unsetDefaultFilter, getDefaultFilter,
  saveUserBet,
} from '../services/userDataService'
import ShareModal from './ShareModal'
import { settlementFields, legRecord } from '../TRACKER_PAGE/settlement'
import '../styles/app-multi-builder.css'

// Saved auto-filter compat: EV8 uses pageType 'multi' with field names minTime/maxTime
// for the hours-until-game range. We standardise on minHours/maxHours internally but
// fall through to the EV8 names on load so legacy filters still work.
const PAGE_TYPE = 'multi'

// ─── AdjustedOddsTip — info icon with hover (desktop) / tap (mobile) tooltip ──

const AdjustedOddsTip = ({ mobile }) => {
  const [open, setOpen] = useState(false)
  if (mobile) {
    return (
      <span className="bc-mb-info-tip" onClick={e => { e.preventDefault(); setOpen(v => !v) }}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="7.25" y="6.5" width="1.5" height="5.5" rx="0.75" fill="currentColor"/>
          <rect x="7.25" y="3.75" width="1.5" height="1.5" rx="0.75" fill="currentColor"/>
        </svg>
        {open && (
          <span className="bc-mb-tooltip bc-mb-tooltip-open">
            Books may shorten odds for correlated legs or apply boosts. Re enter the adjusted odds and the EV% will be recalculated.
          </span>
        )}
      </span>
    )
  }
  return (
    <span className="bc-mb-info-tip">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="7.25" y="6.5" width="1.5" height="5.5" rx="0.75" fill="currentColor"/>
        <rect x="7.25" y="3.75" width="1.5" height="1.5" rx="0.75" fill="currentColor"/>
      </svg>
      <span className="bc-mb-tooltip">
        Books may shorten odds for correlated legs or apply boosts. Re-enter the adjusted odds and the EV% will be recalculated.
      </span>
    </span>
  )
}

// ─── NumericInput — allows empty string while typing, commits on blur ─────────

const NumericInput = ({ value, onChange, defaultValue = 0, ...props }) => {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { setDraft(String(value)) }, [value])
  return (
    <input
      {...props}
      type="number"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        const final = isNaN(n) ? defaultValue : n
        setDraft(String(final))
        onChange(final)
      }}
    />
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// `_milestones` markets are over/under twins of the same prop, dedupe across them.
const baseMarket = (mt) => (mt || '').replace('_milestones', '')

// Strip "Player Name over X" / "over X" → "Player Name" for correlation grouping.
const correlationKey = (opp) => {
  const sel = opp.selection || ''
  const m = sel.match(/^(.+?)\s+(over|under)\s+/i)
  const subject = m ? m[1].trim() : sel
  return `${opp.commence_time}|${subject}|${baseMarket(opp.market_type)}`
}

const formatMarketType = (mt) => {
  if (MARKET_TYPE_MAPPING[mt]) return MARKET_TYPE_MAPPING[mt]
  return (mt || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const evClass = (ev) => {
  if (ev >= 10) return 'bc-mb-ev bc-mb-ev-high'
  if (ev >= 5)  return 'bc-mb-ev bc-mb-ev-mid'
  return 'bc-mb-ev bc-mb-ev-low'
}

const REGION_LABELS = { AU: 'Australia', US: 'United States', EU: 'Europe', EXCHANGE: 'Exchanges' }

const formatGameTime = (ts) => {
  const d = new Date(ts + 'Z')
  const days   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  let hours = d.getHours()
  const mins = d.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}  ·  ${hours}:${mins} ${ampm}`
}

// ─── Custom Select ───────────────────────────────────────────────────────────

function CustomSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selected = options.find(o => o.value === value)

  return (
    <div className="bc-mb-custom-select" ref={ref}>
      <button className={`bc-mb-custom-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span>{selected?.label || value}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {open && (
        <div className="bc-mb-custom-select-menu">
          {options.map(o => (
            <button
              key={o.value}
              className={`bc-mb-custom-select-option${o.value === value ? ' selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false) }}
            >
              {o.label}
              {o.value === value && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function BookmakerSelect({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="bc-mb-custom-select" ref={ref}>
      <button className={`bc-mb-custom-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className="bc-mb-custom-select-trigger-inner">
          {value && BOOKMAKER_LOGOS[value] && (
            <img src={BOOKMAKER_LOGOS[value]} alt={value} className="bc-mb-custom-select-logo" />
          )}
          <span>{value || 'Select bookmaker...'}</span>
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {open && (
        <div className="bc-mb-custom-select-menu">
          {Object.entries(BOOKMAKERS_BY_REGION).map(([region, books]) => (
            <React.Fragment key={region}>
              <div className="bc-mb-custom-select-group">{REGION_LABELS[region]}</div>
              {books.map(b => (
                <button
                  key={b}
                  className={`bc-mb-custom-select-option${b === value ? ' selected' : ''}`}
                  onClick={() => { onChange(value === b ? '' : b); setOpen(false) }}
                >
                  <span className="bc-mb-custom-select-trigger-inner">
                    {BOOKMAKER_LOGOS[b] && <img src={BOOKMAKER_LOGOS[b]} alt={b} className="bc-mb-custom-select-logo" />}
                    {b}
                  </span>
                  {b === value && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EVMultiBuilder({
  isOpen,
  onClose,
  rawOpportunities,
  selectedLegs,
  onLegsChange,
  kellyFraction,
  bankroll,
  roundStakes,
  mobile = false,
}) {
  const { user, oddsFormat } = useAuth() || {}

  const [activeTab,   setActiveTab]   = useState('build')
  const [boostedOdds, setBoostedOdds] = useState('')
  const [stake,       setStake]       = useState('')
  const [shareOpen,   setShareOpen]   = useState(false)
  const multisRef = useRef(null)

  // Auto-build filters
  const [autoSport,            setAutoSport]            = useState(ALL_SPORTS[0])
  const [autoBookmaker,        setAutoBookmaker]        = useState('Sportsbet')
  const [autoMinEV,            setAutoMinEV]            = useState(2)
  const [autoMaxEV,            setAutoMaxEV]            = useState(100)
  const [autoMinOdds,          setAutoMinOdds]          = useState(1.2)
  const [autoMaxOdds,          setAutoMaxOdds]          = useState(4.0)
  const [autoMinLegs,          setAutoMinLegs]          = useState(2)
  const [autoMaxLegs,          setAutoMaxLegs]          = useState(4)
  const [autoMinHours,         setAutoMinHours]         = useState(0)
  const [autoMaxHours,         setAutoMaxHours]         = useState(72)
  const [autoMaxCombinedOdds,  setAutoMaxCombinedOdds]  = useState(20)
  const [autoMinBooks,         setAutoMinBooks]         = useState(5)
  const [autoSGMOnly,          setAutoSGMOnly]          = useState(false)
  const [suggestedMultis,      setSuggestedMultis]      = useState([])
  const [autoError,            setAutoError]            = useState('')

  // Saved filters
  const [savedFilters,        setSavedFilters]        = useState([])
  const [filterName,          setFilterName]          = useState('')
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)

  // ─── Stats ────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (selectedLegs.length === 0) return { combinedOdds: 1, finalOdds: 1, combinedEV: 0, potentialProfit: 0 }
    const combinedOdds = selectedLegs.reduce((t, l) => t * l.odds, 1)
    const boosted = parseFloat(boostedOdds)
    const finalOdds = boosted > 0 ? boosted : combinedOdds
    let evMult = 1
    selectedLegs.forEach(l => { evMult *= (1 + l.ev_percentage / 100) })
    let combinedEV
    if (boosted > 0) {
      const fairOdds = combinedOdds / evMult
      combinedEV = ((finalOdds / fairOdds) - 1) * 100
    } else {
      combinedEV = (evMult - 1) * 100
    }
    const s = parseFloat(stake) || 0
    return { combinedOdds, finalOdds, combinedEV, potentialProfit: s * (finalOdds - 1) }
  }, [selectedLegs, boostedOdds, stake])

  // ─── Kelly stake auto-suggest ─────────────────────────────────────────────

  const suggestStake = useMemo(() => {
    if (selectedLegs.length < 2) return ''
    const fracs = { '1/4': 0.25, '1/2': 0.5, '3/4': 0.75, 'Full': 1 }
    const frac = fracs[kellyFraction] ?? 0.5
    const { finalOdds, combinedEV } = stats
    if (finalOdds <= 1) return ''
    const p = (1 + combinedEV / 100) / finalOdds
    const q = 1 - p
    const b = finalOdds - 1
    const kf = Math.max(0, (b * p - q) / b)
    let s = kf * frac * bankroll
    if (roundStakes === '1') s = Math.round(s)
    else if (roundStakes === '5') s = Math.round(s / 5) * 5
    else s = parseFloat(s.toFixed(2))
    return s > 0 ? String(s) : ''
  }, [selectedLegs, stats, kellyFraction, bankroll, roundStakes])

  useEffect(() => {
    if (selectedLegs.length >= 2) setStake(suggestStake)
    // suggestStake already depends on selectedLegs, so selectedLegs.length is covered transitively
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestStake])

  const legGroups = useMemo(() => {
    const map = new Map()
    selectedLegs.forEach(leg => {
      const key = `${leg.commence_time}|${leg.match}`
      if (!map.has(key)) map.set(key, { match: leg.match, commence_time: leg.commence_time, legs: [] })
      map.get(key).legs.push(leg)
    })
    return [...map.values()].sort((a, b) => new Date(a.commence_time + 'Z') - new Date(b.commence_time + 'Z'))
  }, [selectedLegs])

  // ─── Load saved filters (compat with EV8 format) ──────────────────────────

  useEffect(() => {
    if (!user?.id || !isOpen) return
    getUserFilters(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user?.id, isOpen])

  useEffect(() => {
    if (!user?.id || !isOpen || defaultFilterLoaded) return
    getDefaultFilter(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) applyFilterData(data.filter_data)
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, isOpen, defaultFilterLoaded])

  // ─── Filter helpers ───────────────────────────────────────────────────────

  // Reads both new (`minHours/maxHours`) and EV8-legacy (`minTime/maxTime`) field names.
  const applyFilterData = (fd) => {
    if (fd.sport)                   setAutoSport(fd.sport)
    if (fd.bookmaker !== undefined) setAutoBookmaker(fd.bookmaker)
    if (fd.minEV !== undefined)     setAutoMinEV(fd.minEV)
    if (fd.maxEV !== undefined)     setAutoMaxEV(fd.maxEV)
    if (fd.minOdds !== undefined)   setAutoMinOdds(fd.minOdds)
    if (fd.maxOdds !== undefined)   setAutoMaxOdds(fd.maxOdds)
    if (fd.minLegs !== undefined)   setAutoMinLegs(fd.minLegs)
    if (fd.maxLegs !== undefined)   setAutoMaxLegs(fd.maxLegs)
    const minHours = fd.minHours ?? fd.minTime
    const maxHours = fd.maxHours ?? fd.maxTime
    if (minHours !== undefined) setAutoMinHours(minHours)
    if (maxHours !== undefined) setAutoMaxHours(maxHours)
    if (fd.maxCombinedOdds !== undefined) setAutoMaxCombinedOdds(fd.maxCombinedOdds)
    if (fd.minBooks !== undefined)        setAutoMinBooks(fd.minBooks)
    if (fd.sgmOnly !== undefined)         setAutoSGMOnly(fd.sgmOnly)
  }

  // Save under EV8's field names so old EV page can read them too.
  const buildFilterData = () => ({
    sport: autoSport,
    bookmaker: autoBookmaker,
    minEV: autoMinEV, maxEV: autoMaxEV,
    minOdds: autoMinOdds, maxOdds: autoMaxOdds,
    minLegs: autoMinLegs, maxLegs: autoMaxLegs,
    minTime: autoMinHours, maxTime: autoMaxHours,
    maxCombinedOdds: autoMaxCombinedOdds,
    minBooks: autoMinBooks,
    sgmOnly: autoSGMOnly,
  })

  const handleSaveFilter = async () => {
    if (!filterName.trim() || !user?.id) return
    const { data } = await saveUserFilter(user.id, PAGE_TYPE, filterName.trim(), buildFilterData())
    if (data) {
      setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
      setFilterName('')
    }
  }

  const handleDeleteFilter = async (id) => {
    await deleteUserFilter(id)
    setSavedFilters(prev => prev.filter(f => f.id !== id))
  }

  const handleToggleDefault = async (id) => {
    const f = savedFilters.find(f => f.id === id)
    if (!f) return
    if (f.isDefault) {
      await unsetDefaultFilter(user.id, PAGE_TYPE, id)
    } else {
      await setDefaultFilter(user.id, PAGE_TYPE, id)
    }
    setSavedFilters(prev => prev.map(sf => ({ ...sf, isDefault: sf.id === id ? !f.isDefault : false })))
  }

  const resetFilters = () => {
    setAutoSport(ALL_SPORTS[0])
    setAutoBookmaker('Sportsbet')
    setAutoMinEV(2); setAutoMaxEV(100)
    setAutoMinOdds(1.2); setAutoMaxOdds(4.0)
    setAutoMinLegs(2); setAutoMaxLegs(4)
    setAutoMinHours(0); setAutoMaxHours(72)
    setAutoMaxCombinedOdds(20)
    setAutoMinBooks(5)
    setAutoSGMOnly(false)
  }

  // ─── Auto build ───────────────────────────────────────────────────────────

  const generateMultis = () => {
    setAutoError('')
    setSuggestedMultis([])
    if (!autoBookmaker) { setAutoError('Please select a bookmaker.'); return }

    let eligible = (rawOpportunities || []).filter(opp => {
      if (opp.sport !== autoSport) return false
      if (opp.bookmaker !== autoBookmaker) return false
      if (opp.ev_percentage < autoMinEV || opp.ev_percentage > autoMaxEV) return false
      if (opp.original_odds < autoMinOdds || opp.original_odds > autoMaxOdds) return false
      const hours = (new Date(opp.commence_time + 'Z') - new Date()) / 3600000
      if (hours < autoMinHours || hours > autoMaxHours) return false
      if ((opp.bookmaker_count || 1) < autoMinBooks) return false
      return true
    })

    if (autoSGMOnly) {
      const matchGroups = {}
      eligible.forEach(opp => {
        if (!matchGroups[opp.match]) matchGroups[opp.match] = []
        matchGroups[opp.match].push(opp)
      })
      const bestMatch = Object.values(matchGroups).sort((a, b) => b.length - a.length)[0]
      if (!bestMatch || bestMatch.length < autoMinLegs) {
        setAutoError(`Not enough legs in a single match for SGM. Best match has ${bestMatch?.length ?? 0} leg(s). Try relaxing filters or disabling SGM.`)
        return
      }
      eligible = bestMatch
    }

    if (eligible.length < autoMinLegs) {
      setAutoError(`Only ${eligible.length} eligible leg(s) found. Try relaxing your filters.`)
      return
    }

    const sorted = [...eligible].sort((a, b) => b.ev_percentage - a.ev_percentage)
    const candidates = []

    for (let target = autoMinLegs; target <= autoMaxLegs; target++) {
      const usedAcross = new Set()
      for (let attempt = 0; attempt < 5; attempt++) {
        const usedInMulti = new Set()
        const legs = []
        for (const opp of sorted) {
          if (legs.length >= target) break
          const corrKey = correlationKey(opp)
          if (usedInMulti.has(corrKey) || usedAcross.has(corrKey)) continue
          legs.push({
            _key: `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`,
            selection: opp.selection,
            match: opp.match,
            market_type: opp.market_type,
            sport: opp.sport,
            commence_time: opp.commence_time,
            bookmaker: opp.bookmaker,
            game_url: opp.game_url || null,
            odds: opp.original_odds,
            ev_percentage: opp.ev_percentage,
            // Same settlement fields the manually added legs carry, so an
            // auto-built multi is no less settleable than a hand-built one.
            ...settlementFields(opp),
          })
          usedInMulti.add(corrKey)
        }
        if (legs.length < target) break
        const combinedOdds = legs.reduce((t, l) => t * l.odds, 1)
        let evMult = 1
        legs.forEach(l => { evMult *= (1 + l.ev_percentage / 100) })
        candidates.push({ legs, combinedOdds, combinedEV: (evMult - 1) * 100 })
        usedInMulti.forEach(k => usedAcross.add(k))
      }
    }

    const valid = candidates.filter(m => m.combinedOdds <= autoMaxCombinedOdds)
    if (valid.length === 0) {
      if (candidates.length > 0) {
        const lowest = Math.min(...candidates.map(m => m.combinedOdds))
        setAutoError(`No multis within combined odds limit (${autoMaxCombinedOdds}x). Lowest found: ${lowest.toFixed(2)}x. Try raising the limit.`)
      } else {
        setAutoError('Not enough uncorrelated legs with these filters. Try relaxing EV, odds, or bookmaker selection.')
      }
      return
    }

    setSuggestedMultis(valid.sort((a, b) => b.combinedEV - a.combinedEV).slice(0, 3))
    setTimeout(() => multisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
  }

  const selectMulti = (m) => {
    onLegsChange(m.legs)
    setSuggestedMultis([])
    setActiveTab('build')
  }

  // ─── Leg editing ──────────────────────────────────────────────────────────

  const updateLegOdds = (key, val) => {
    const parsed = parseFloat(val)
    if (!isNaN(parsed) && parsed > 0) {
      onLegsChange(selectedLegs.map(l => {
        if (l._key !== key) return l
        const fairOdds = l.fair_odds || l.odds / (1 + l.ev_percentage / 100)
        return { ...l, odds: parsed, ev_percentage: (parsed / fairOdds - 1) * 100 }
      }))
    }
  }

  const removeLeg = (key) => onLegsChange(selectedLegs.filter(l => l._key !== key))

  const clearAll = () => { onLegsChange([]); setStake(''); setBoostedOdds('') }

  // ─── Save to tracker ──────────────────────────────────────────────────────

  const saveToTracker = async () => {
    if (!user?.id || selectedLegs.length < 2) return
    const s = parseFloat(stake)
    if (!s || s <= 0) return
    const { finalOdds, combinedEV } = stats
    const bet = {
      type: 'EV',
      bet_type_tag: 'multi',
      sport: selectedLegs[0].sport,
      match: `${selectedLegs.length}-Leg Multi`,
      selection: selectedLegs.map(l => l.selection).join(' / '),
      bookmaker: selectedLegs[0].bookmaker,
      market_type: 'multi',
      // Each leg keeps the structured fields a single carries, which is what
      // lets settlement resolve them one game at a time. Without this the multi
      // is only a sentence and can never be settled automatically.
      legs: selectedLegs.map(legRecord),
      odds: parseFloat(finalOdds.toFixed(2)),
      stake: s,
      ev_percentage: parseFloat(combinedEV.toFixed(2)),
      status: 'pending',
      actual_profit: null,
      placed_at: new Date().toISOString(),
      settled_at: null,
    }
    const { error } = await saveUserBet(user.id, bet)
    if (!error) { clearAll(); onClose() }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!isOpen) return null

  return (
    <>
      <div className="bc-mb-overlay" onClick={onClose} />
      <div className={mobile ? 'bc-mb-modal bc-mb-modal-mobile' : 'bc-mb-modal'} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bc-mb-header">
          <div className="bc-mb-header-left">
            <h2 className="bc-mb-title">Multi Builder</h2>
          </div>
          <button className="bc-mb-close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="bc-mb-tabs">
          <button className={`bc-mb-tab${activeTab === 'build' ? ' active' : ''}`} onClick={() => setActiveTab('build')}>
            Build
          </button>
          <button className={`bc-mb-tab${activeTab === 'auto' ? ' active' : ''}`} onClick={() => setActiveTab('auto')}>
            Auto Build
          </button>
        </div>

        {/* Body */}
        <div className="bc-mb-body">

          {/* ── BUILD TAB ── */}
          {activeTab === 'build' && (
            selectedLegs.length === 0 ? (
              <div className="bc-mb-empty">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="4"/>
                </svg>
                <p className="bc-mb-empty-title">No legs added yet</p>
                <p className="bc-mb-empty-sub">Click the + button on any table row, or use <strong>Auto Build</strong></p>
              </div>
            ) : (
              <div className="bc-mb-build-content">

                <div className="bc-mb-legs-with-banner">
                <div className="bc-mb-legs">
                  {legGroups.map((group, gi) => (
                    <div key={`${group.commence_time}|${group.match}`} className="bc-mb-leg-group">

                      <div className="bc-mb-leg-group-header">
                        <span className="bc-mb-leg-group-match">{group.match}</span>
                        <span className="bc-mb-leg-group-time">{formatGameTime(group.commence_time)}</span>
                      </div>

                      {group.legs.map(leg => (
                        <div key={leg._key} className="bc-mb-leg">
                          {/* Legs are grouped under a game header, so this is not
                              repeating the fixture — it says which SIDE of it the
                              leg is on: the player's face, the backed team's
                              crest, or both crests for a match total. */}
                          <div className="bc-mb-ev-subject">
                            <SubjectImage opp={leg} />
                          </div>

                          {/* Info */}
                          <div className="bc-mb-leg-body">
                            <div className="bc-mb-leg-row1">
                              <span className="bc-mb-leg-player">{leg.selection}</span>
                            </div>
                            <div className="bc-mb-leg-row2">
                              <span className="bc-mb-leg-market">{formatMarketType(leg.market_type)}</span>
                            </div>
                          </div>

                          {/* Right: book + odds + remove */}
                          <div className="bc-mb-leg-right">
                            {BOOKMAKER_LOGOS[leg.bookmaker]
                              ? <img src={BOOKMAKER_LOGOS[leg.bookmaker]} alt={leg.bookmaker} className="bc-mb-leg-book-logo" data-tooltip={leg.bookmaker} onClick={() => leg.game_url && window.open(leg.game_url, '_blank')} style={{ cursor: leg.game_url ? 'pointer' : 'default' }} />
                              : <span className="bc-mb-leg-book-name">{leg.bookmaker}</span>
                            }
                            <input type="number" step="0.01" value={leg.odds} onChange={e => updateLegOdds(leg._key, e.target.value)} className="bc-mb-leg-odds-input" />
                            {!mobile && (
                              <span className={`${evClass(leg.ev_percentage)} bc-mb-leg-ev-badge`}>
                                {leg.ev_percentage > 0 ? '+' : ''}{leg.ev_percentage.toFixed(1)}%
                              </span>
                            )}
                            <button className="bc-mb-leg-remove" onClick={() => removeLeg(leg._key)}>×</button>
                          </div>

                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Stats banner */}
                <div className="bc-mb-banner">
                  <div className="bc-mb-banner-left">
                    {BOOKMAKER_LOGOS[selectedLegs[0]?.bookmaker] && (
                      <img src={BOOKMAKER_LOGOS[selectedLegs[0].bookmaker]} alt={selectedLegs[0].bookmaker} className="bc-mb-stats-bar-logo" />
                    )}
                    <span className="bc-mb-stats-bar-book-name">{selectedLegs[0]?.bookmaker}</span>
                    <div className="bc-mb-banner-vdivider" />
                    <span className="bc-mb-banner-odds">{convertOdds ? convertOdds(stats.finalOdds, oddsFormat) : stats.finalOdds.toFixed(2)}</span>
                  </div>
                  <div className="bc-mb-banner-right">
                    <span className={`bc-mb-ev ${stats.combinedEV >= 10 ? 'bc-mb-ev-high' : stats.combinedEV >= 5 ? 'bc-mb-ev-mid' : 'bc-mb-ev-low'}`}>
                      {stats.combinedEV > 0 ? '+' : ''}{stats.combinedEV.toFixed(1)}%
                    </span>
                    <span className="bc-mb-result-leg-badge">{selectedLegs.length} Leg Multi</span>
                  </div>
                </div>
                </div>

                {/* Inputs */}
                <div className="bc-mb-inputs">
                  <div className="bc-mb-input-group">
                    <label className="bc-mb-input-label">
                      Adjusted Odds <span className="bc-mb-input-hint">(optional)</span>
                      <AdjustedOddsTip mobile={mobile} />
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={convertOdds ? convertOdds(stats.combinedOdds, oddsFormat) : stats.combinedOdds.toFixed(2)}
                      value={boostedOdds}
                      onChange={e => setBoostedOdds(e.target.value)}
                      className="bc-mb-input"
                    />
                  </div>
                  <div className="bc-mb-input-group">
                    <label className="bc-mb-input-label">Stake</label>
                    <div className="bc-mb-input-prefix">
                      <span className="bc-mb-prefix">$</span>
                      <input
                        type="number"
                        step="1"
                        placeholder="0"
                        value={stake}
                        onChange={e => setStake(e.target.value)}
                        className="bc-mb-input bc-mb-input-has-prefix"
                      />
                    </div>
                  </div>
                  <div className="bc-mb-input-group">
                    <label className="bc-mb-input-label">Profit</label>
                    <div className={`bc-mb-profit-display${parseFloat(stake) > 0 ? ' has-value' : ''}`}>
                      <span className="bc-mb-profit-val">
                        {parseFloat(stake) > 0 ? `$${stats.potentialProfit.toFixed(2)}` : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bc-mb-build-actions">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="bc-btn ghost" onClick={clearAll}>Clear All</button>
                    <button
                      className="bc-mb-share-btn"
                      onClick={() => setShareOpen(true)}
                      disabled={selectedLegs.length < 2}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/>
                        <circle cx="6" cy="12" r="3"/>
                        <circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      Share
                    </button>
                  </div>
                  <button
                    className="bc-btn primary"
                    onClick={saveToTracker}
                    disabled={selectedLegs.length < 2 || !stake || parseFloat(stake) <= 0}
                  >
                    Save to Tracker
                  </button>
                </div>
              </div>
            )
          )}

          {/* ── AUTO BUILD TAB ── */}
          {activeTab === 'auto' && (
            <div className="bc-mb-auto-content">

              {/* Saved filters */}
              {savedFilters.length > 0 && (
                <div className="bc-filter-section">
                  <span className="bc-filter-label">Saved Filters</span>
                  <div className="bc-saved-list">
                    {savedFilters.map(f => (
                      <div key={f.id} className={`bc-saved-item${f.isDefault ? ' is-default' : ''}`}>
                        <button className="bc-saved-load" onClick={() => applyFilterData(f)}>{f.name}</button>
                        <div className="bc-saved-actions">
                          <button className={`bc-saved-star${f.isDefault ? ' active' : ''}`} onClick={() => handleToggleDefault(f.id)} title={f.isDefault ? 'Remove default' : 'Set as default'}>★</button>
                          <button className="bc-saved-delete" onClick={() => handleDeleteFilter(f.id)} title="Delete">×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sport / Bookmaker */}
              <div className="bc-filter-section">
                <div className="bc-mb-dropdown-group">
                  <div className="bc-mb-dropdown-item">
                    <span className="bc-filter-label">Sport</span>
                    <CustomSelect
                      value={autoSport}
                      onChange={setAutoSport}
                      options={ALL_SPORTS.map(s => ({ value: s, label: SPORT_DISPLAY_NAMES[s] || s }))}
                    />
                  </div>
                  <div className="bc-mb-dropdown-item">
                    <span className="bc-filter-label">Bookmaker</span>
                    <BookmakerSelect value={autoBookmaker} onChange={setAutoBookmaker} />
                  </div>
                </div>
              </div>

              {/* Row 1: Legs | Odds/Leg */}
              <div className="bc-filter-section">
                <div className="bc-mb-filter-2col">
                  <div className="bc-mb-filter-col">
                    <span className="bc-mb-filter-heading">Legs</span>
                    <div className="bc-range-row">
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Min</span>
                        <NumericInput min="2" max="10" value={autoMinLegs} defaultValue={2} onChange={v => setAutoMinLegs(v)} className="bc-range-input" />
                      </div>
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Max</span>
                        <NumericInput min="2" max="10" value={autoMaxLegs} defaultValue={10} onChange={v => setAutoMaxLegs(v)} className="bc-range-input" />
                      </div>
                    </div>
                  </div>
                  <div className="bc-mb-filter-col">
                    <span className="bc-mb-filter-heading">Odds / Leg</span>
                    <div className="bc-range-row">
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Min</span>
                        <NumericInput step="0.1" value={autoMinOdds} defaultValue={1} onChange={v => setAutoMinOdds(v)} className="bc-range-input" />
                      </div>
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Max</span>
                        <NumericInput step="0.1" value={autoMaxOdds} defaultValue={10} onChange={v => setAutoMaxOdds(v)} className="bc-range-input" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: EV % | Starts In */}
              <div className="bc-filter-section">
                <div className="bc-mb-filter-2col">
                  <div className="bc-mb-filter-col">
                    <span className="bc-mb-filter-heading">EV %</span>
                    <div className="bc-range-row">
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Min</span>
                        <NumericInput step="0.5" value={autoMinEV} defaultValue={0} onChange={v => setAutoMinEV(v)} className="bc-range-input" />
                      </div>
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Max</span>
                        <NumericInput step="0.5" value={autoMaxEV} defaultValue={100} onChange={v => setAutoMaxEV(v)} className="bc-range-input" />
                      </div>
                    </div>
                  </div>
                  <div className="bc-mb-filter-col">
                    <span className="bc-mb-filter-heading">Starts In (hrs)</span>
                    <div className="bc-range-row">
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Min</span>
                        <NumericInput step="1" min="0" value={autoMinHours} defaultValue={0} onChange={v => setAutoMinHours(v)} className="bc-range-input" />
                      </div>
                      <div className="bc-range-field">
                        <span className="bc-range-sublabel">Max</span>
                        <NumericInput step="1" min="0" value={autoMaxHours} defaultValue={120} onChange={v => setAutoMaxHours(v)} className="bc-range-input" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Min Books | Max Combined Odds */}
              <div className="bc-filter-section">
                <div className="bc-mb-filter-2col">
                  <div className="bc-mb-filter-col">
                    <span className="bc-mb-filter-heading">Min Books</span>
                    <NumericInput min="1" max="20" value={autoMinBooks} defaultValue={1} onChange={v => setAutoMinBooks(v)} className="bc-range-input" />
                  </div>
                  <div className="bc-mb-filter-col">
                    <span className="bc-mb-filter-heading">Max Combined Odds</span>
                    <NumericInput step="1" min="2" value={autoMaxCombinedOdds} defaultValue={2} onChange={v => setAutoMaxCombinedOdds(v)} className="bc-range-input" />
                  </div>
                </div>
              </div>

              {/* SGM toggle */}
              <div className="bc-mb-sgm-row">
                <label className="bc-toggle">
                  <input type="checkbox" checked={autoSGMOnly} onChange={e => setAutoSGMOnly(e.target.checked)} />
                  <span className="bc-toggle-track" />
                </label>
                <span className="bc-mb-sgm-label">SGM Only (Same Game Multi)</span>
              </div>

              {/* Save + Generate */}
              <div className="bc-mb-auto-footer">
                <div className="bc-mb-auto-footer-top">
                  <input
                    className="bc-range-input"
                    placeholder="Filter name…"
                    value={filterName}
                    onChange={e => setFilterName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveFilter()}
                  />
                  <button className="bc-btn" onClick={handleSaveFilter} disabled={!filterName.trim()}>Save Current</button>
                  <button className="bc-btn" onClick={resetFilters}>Reset</button>
                </div>
                <button className="bc-btn primary bc-mb-generate-btn" onClick={generateMultis}>
                  Generate Multis
                </button>
              </div>

              {/* Error */}
              {autoError && <div className="bc-mb-auto-error">{autoError}</div>}

              {/* Suggested multis */}
              {suggestedMultis.length > 0 && (
                <div className="bc-mb-multis" ref={multisRef}>
                  <span className="bc-filter-label">Top {suggestedMultis.length} Multi{suggestedMultis.length > 1 ? 's' : ''}</span>
                  {suggestedMultis.map((m, i) => (
                    <div key={i} className="bc-mb-legs bc-mb-multi-result">

                      {/* Card header */}
                      <div className="bc-mb-banner">
                        <div className="bc-mb-banner-left">
                          {BOOKMAKER_LOGOS[m.legs[0]?.bookmaker] && (
                            <img src={BOOKMAKER_LOGOS[m.legs[0].bookmaker]} alt={m.legs[0].bookmaker} className="bc-mb-stats-bar-logo" />
                          )}
                          <span className="bc-mb-stats-bar-book-name">{m.legs[0]?.bookmaker}</span>
                          <div className="bc-mb-banner-vdivider" />
                          <span className="bc-mb-banner-odds">{convertOdds ? convertOdds(m.combinedOdds, oddsFormat) : m.combinedOdds.toFixed(2)}</span>
                        </div>
                        <div className="bc-mb-banner-right">
                          <span className={`bc-mb-ev ${m.combinedEV >= 10 ? 'bc-mb-ev-high' : m.combinedEV >= 5 ? 'bc-mb-ev-mid' : 'bc-mb-ev-low'}`}>
                            {m.combinedEV > 0 ? '+' : ''}{m.combinedEV.toFixed(1)}%
                          </span>
                          <span className="bc-mb-result-leg-badge">{m.legs.length} Leg Multi</span>
                        </div>
                      </div>

                      {/* Legs grouped by game */}
                      {(() => {
                        const gameMap = new Map()
                        m.legs.forEach(leg => {
                          const key = `${leg.commence_time}|${leg.match}`
                          if (!gameMap.has(key)) gameMap.set(key, { match: leg.match, commence_time: leg.commence_time, legs: [] })
                          gameMap.get(key).legs.push(leg)
                        })
                        return [...gameMap.values()].sort((a, b) => new Date(a.commence_time + 'Z') - new Date(b.commence_time + 'Z')).map(group => (
                          <div key={`${group.commence_time}|${group.match}`} className="bc-mb-leg-group">
                            <div className="bc-mb-leg-group-header">
                              <span className="bc-mb-leg-group-match">{group.match}</span>
                              <span className="bc-mb-leg-group-time">{formatGameTime(group.commence_time)}</span>
                            </div>
                            {group.legs.map((leg, j) => (
                              <div key={j} className="bc-mb-leg bc-mb-leg-compact">
                                <div className="bc-mb-ev-subject">
                                  <SubjectImage opp={leg} />
                                </div>
                                <div className="bc-mb-leg-body">
                                  <div className="bc-mb-leg-row1"><span className="bc-mb-leg-player">{leg.selection}</span></div>
                                  <div className="bc-mb-leg-row2">
                                    <span className="bc-mb-leg-market">{formatMarketType(leg.market_type)}</span>
                                  </div>
                                </div>
                                <div className="bc-mb-leg-right">
                                  {BOOKMAKER_LOGOS[leg.bookmaker]
                                    ? <img src={BOOKMAKER_LOGOS[leg.bookmaker]} alt={leg.bookmaker} className="bc-mb-leg-book-logo" data-tooltip={leg.bookmaker} />
                                    : <span className="bc-mb-leg-book-name">{leg.bookmaker}</span>}
                                  <span className="bc-mb-result-odds">{convertOdds ? convertOdds(leg.odds, oddsFormat) : leg.odds.toFixed(2)}</span>
                                  {!mobile && (
                                    <span className={`${evClass(leg.ev_percentage)} bc-mb-leg-ev-badge`}>
                                      {leg.ev_percentage > 0 ? '+' : ''}{leg.ev_percentage.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      })()}

                      <button className="bc-btn primary bc-mb-use-btn-mobile" onClick={() => selectMulti(m)}>Use This Multi</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {shareOpen && (
        <ShareModal
          multi={{
            legs: selectedLegs,
            finalOdds: stats.finalOdds,
            combinedEV: stats.combinedEV
          }}
          onClose={() => setShareOpen(false)}
        />
      )}
    </>
  )
}
